import axios, { AxiosError } from 'axios'
import JsSHA1 from 'jssha/dist/sha1'
import { v4 as uuidv4 } from 'uuid'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useEventBus } from '@/composables/event_bus'
import TokenService from '@/services/user/token'

import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
  Method,
} from 'axios'
import type { EventSourceMessage } from '@microsoft/fetch-event-source/lib/cjs/parse'

type ErrorListenerCallback = (error: any, correlationId: string) => void

interface SessionHandlers {
  refreshToken: () => Promise<string>
  expireSession: () => Promise<void>
}

class UnauthorizedError extends Error {}
class RefreshTokenError extends Error {}

export class EmptyResponseError extends Error {
  constructor(public response: AxiosResponse) {
    super('Empty or non-JSON response body')
    this.name = 'EmptyResponseError'
  }
}

export interface CancelSources {
  [key: string]: CancelTokenSource
}

export interface StreamAbortControllers {
  [key: string]: AbortController
}

export interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  expected404?: boolean
  skip401Interceptor?: boolean
  _retry?: boolean
}

export interface RequestOptions {
  headers?: Record<string, string>
  allowConcurrentRequests?: boolean
  expected404?: boolean
  skip401Interceptor?: boolean
}

export const AUTH_HEADER = 'Authorization'
export const CORRELATION_ID_HEADER = 'X-Correlation-ID'
export const ROOT_USER_ID_HEADER = 'X-Root-User-ID'
export const ENTITY_ID_HEADER = 'X-Entity-ID'
export const CLIENT_ACTION_ID_HEADER = 'X-Client-Action-Id'

const CANCEL_REQUEST_MESSAGE = 'Request canceled'
const CANNOT_OPEN_DATA_STREAM_MESSAGE = 'Cannot open data stream'

const eventBus = useEventBus()

class ApiService {
  private refreshPromise: Promise<string> | null = null
  private baseUrlV1 = ''
  private baseUrlV2 = ''
  private baseUrlV3 = ''
  private axiosInstance: AxiosInstance
  private _401interceptor: number | null = null
  private cancelSources: CancelSources = {}
  private streamAbortControllers: StreamAbortControllers = {}
  private errorListener: ErrorListenerCallback | null = null
  private sessionHandlers: SessionHandlers | null = null
  private apiHeaders: Record<string, string> = {}

  private static instance: ApiService

  private constructor() {
    this.axiosInstance = axios.create({
      // headers: {'X-Custom-Header': 'foobar'}
    })
    this.mountApiHeadersInterceptor()
    this.mountErrorInterceptor()
    this.mountRefreshTokenExpirationInterceptor()
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService()
    }

    return ApiService.instance
  }

  public init(baseUrlV1: string, baseUrlV2: string, baseUrlV3 = '') {
    this.baseUrlV1 = baseUrlV1
    this.baseUrlV2 = baseUrlV2
    this.baseUrlV3 = baseUrlV3
  }

  public setAuthHeader() {
    this.setApiHeader(AUTH_HEADER, `${TokenService.getAuthToken()}`)
  }

  public setRootUserHeader(rootUserID: string) {
    this.setApiHeader(ROOT_USER_ID_HEADER, rootUserID)
  }

  public setEntityHeader(entityID: string) {
    this.setApiHeader(ENTITY_ID_HEADER, entityID)
  }

  protected setApiHeader(key: string, value: string) {
    this.apiHeaders[key] = value
  }

  public removeApiHeaders() {
    this.apiHeaders = {}
  }

  /**
   * A signed-in tab sends the access token that is in storage now: another
   * tab shares the storage and may have renewed the token since.
   */
  private adoptStoredAuthToken() {
    const storedToken = TokenService.getAuthToken()
    if (storedToken && this.apiHeaders[AUTH_HEADER]) {
      this.apiHeaders[AUTH_HEADER] = storedToken
    }
  }

  /**
   * Promise coalescing: if a refresh is already in flight, return the same
   * promise instead of issuing a second request. Concurrent 401s therefore
   * trigger only one token refresh; all callers await the same resolution.
   *
   * A refresh answered with 401 ends the session; a transient failure
   * (network, 5xx) keeps it. Without a stored refresh token nothing is sent;
   * a tab still carrying the auth header (session ended in another tab) is
   * logged out.
   */
  private async ensureRefresh(): Promise<string> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }
    const { sessionHandlers } = this
    if (!sessionHandlers) {
      throw new RefreshTokenError()
    }

    if (!TokenService.getRefreshToken()) {
      if (this.apiHeaders[AUTH_HEADER]) {
        await sessionHandlers.expireSession()
      }
      throw new RefreshTokenError()
    }

    this.refreshPromise = sessionHandlers
      .refreshToken()
      .catch(async (error) => {
        if (error instanceof AxiosError && error.response?.status === 401) {
          console.warn('Refresh token failed! Auth failure — logging out.')
          await sessionHandlers.expireSession()
        } else {
          console.warn(
            'Refresh token failed! Transient error — session preserved.'
          )
        }
        throw error
      })
      .finally(() => {
        this.refreshPromise = null
      })
    return this.refreshPromise
  }

  public async get(
    resource: string,
    options?: RequestOptions
  ): Promise<AxiosResponse> {
    return this.performRequest(resource, 'GET', null, options)
  }

  public async post(
    resource: string,
    data: any,
    options?: RequestOptions
  ): Promise<AxiosResponse> {
    return this.performRequest(resource, 'POST', data, options)
  }

  public async put(
    resource: string,
    data: any,
    options?: RequestOptions
  ): Promise<AxiosResponse> {
    return this.performRequest(resource, 'PUT', data, options)
  }

  public async patch(
    resource: string,
    data: any,
    options?: RequestOptions
  ): Promise<AxiosResponse> {
    return this.performRequest(resource, 'PATCH', data, options)
  }

  public async delete(
    resource: string,
    data?: any,
    options?: RequestOptions
  ): Promise<AxiosResponse> {
    return this.performRequest(resource, 'DELETE', data, options)
  }

  public async getStream(
    resource: string,
    method: 'GET' | 'POST',
    onMessage: (ev: EventSourceMessage) => void,
    onClose?: () => void,
    body?: any,
    abortController?: AbortController
  ): Promise<void> {
    const hash = ApiService.getHash(resource, method)

    let _abortController: AbortController
    if (!abortController) {
      this.abortStreamByHash(hash)
      _abortController = new AbortController()
      this.streamAbortControllers[hash] = _abortController
    } else {
      _abortController = abortController
    }

    const url = this.getResourceUrl(resource)
    const correlationID = uuidv4()
    const data = JSON.stringify(body)

    const doStream = async (retried = false): Promise<void> => {
      this.adoptStoredAuthToken()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        [CORRELATION_ID_HEADER]: correlationID,
        ...this.apiHeaders,
      }

      try {
        await fetchEventSource(url, {
          method,
          headers,
          onopen: async (response) => {
            if (response.ok) {
              return
            }
            if (response.status === 401) {
              try {
                await this.ensureRefresh()
              } catch {
                throw new RefreshTokenError()
              }
              throw new UnauthorizedError()
            }
            throw new Error(CANNOT_OPEN_DATA_STREAM_MESSAGE)
          },
          onmessage: onMessage,
          onclose: () => {
            delete this.streamAbortControllers[hash]
            if (onClose) {
              onClose()
            }
          },
          onerror: (err) => {
            if (
              this.errorListener &&
              !(err instanceof UnauthorizedError) &&
              !(err instanceof RefreshTokenError)
            ) {
              this.errorListener(
                {
                  name: err.name,
                  message: err.message,
                  stack: err.stack,
                  config: {
                    url,
                    method,
                    data,
                  },
                },
                correlationID
              )
            }
            throw err
          },
          body: data,
          signal: _abortController.signal,
          openWhenHidden: true,
        })
      } catch (err) {
        if (err instanceof UnauthorizedError && !retried) {
          return doStream(true)
        }
        throw err
      }
    }

    return doStream()
  }

  public abortStreamByUrl(resource: string, method: 'GET' | 'POST') {
    const hash = ApiService.getHash(resource, method)
    this.abortStreamByHash(hash)
  }

  public abortStreamByHash(hash: string) {
    if (hash in this.streamAbortControllers) {
      this.streamAbortControllers[hash].abort()
      delete this.streamAbortControllers[hash]
    }
  }

  public async customRequest(
    data: CustomAxiosRequestConfig
  ): Promise<AxiosResponse> {
    return this.axiosInstance.request(data)
  }

  protected async performRequest(
    resource: string,
    method: Method,
    data?: any,
    options?: RequestOptions
  ): Promise<AxiosResponse> {
    const {
      headers,
      allowConcurrentRequests,
      expected404,
      skip401Interceptor,
    } = options ?? {}
    const url = this.getResourceUrl(resource)
    const hash = ApiService.getHash(url, method)

    /**
     * Cancel the ongoing previous request to the same endpoint,
     * because if it is slower than the new one,
     * it will overwrite the required data.
     */
    if (!allowConcurrentRequests && hash in this.cancelSources) {
      this.cancelSources[hash].cancel(CANCEL_REQUEST_MESSAGE)
    }

    const cancelSource = axios.CancelToken.source()
    this.cancelSources[hash] = cancelSource

    try {
      const response = await this.customRequest({
        url,
        method,
        data,
        headers,
        cancelToken: cancelSource?.token,
        expected404,
        skip401Interceptor,
      })
      delete this.cancelSources[hash]
      return response
    } catch (e) {
      if (!axios.isCancel(e)) {
        delete this.cancelSources[hash]
      }
      if (
        expected404 &&
        e instanceof AxiosError &&
        e.response?.status === 404
      ) {
        return e.response
      }
      throw e
    }
  }

  public async liveCheck() {
    try {
      await this.performRequest(`?_=${Date.now()}`, 'HEAD')
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      console.warn(
        'Failed to get HEAD of index. Probably undergoing maintenance.'
      )
    }
  }

  public cancelRequests(resource: string, method: Method) {
    const url = this.getResourceUrl(resource)
    const hash = ApiService.getHash(url, method)

    if (hash in this.cancelSources) {
      this.cancelSources[hash].cancel(CANCEL_REQUEST_MESSAGE)
      delete this.cancelSources[hash]
    }
  }

  public cancelCurrentRequests() {
    Object.keys(this.cancelSources).forEach((key) => {
      this.cancelSources[key].cancel(CANCEL_REQUEST_MESSAGE)
      delete this.cancelSources[key]
    })
  }

  public getResourceUrl(resource: string): string {
    if (resource.indexOf('://') !== -1 || resource == '/version.json') {
      return resource
    } else if (resource.indexOf('/v3/') !== -1) {
      return `${this.baseUrlV3}${resource}`
    } else if (resource.indexOf('/v2/') !== -1) {
      return `${this.baseUrlV2}${resource}`
    } else {
      return `${this.baseUrlV1}${resource}`
    }
  }

  private isApiDestination(config: AxiosRequestConfig): boolean {
    const origin = window.origin
    const url = new URL(this.axiosInstance.getUri(config), origin)
    const destination = `${url.origin}${url.pathname}/`
    return [
      this.getResourceUrl('/api/v1/'),
      this.getResourceUrl('/api/v2/'),
      this.getResourceUrl('/api/v3/'),
    ].some((base) => destination.startsWith(new URL(base, origin).href))
  }

  private mountApiHeadersInterceptor() {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Add API headers only for requests to configured API endpoints.
        if (this.isApiDestination(config)) {
          this.adoptStoredAuthToken()
          // Global apiHeaders first, then per-request headers override them
          config.headers = Object.assign({}, this.apiHeaders, config.headers)
          config.headers[CORRELATION_ID_HEADER] = uuidv4()
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  private mountErrorInterceptor() {
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // A resolved 200 whose body isn't a JSON object (an empty body from a
        // proxy/gateway, an HTML auth page from a VPN/SSO layer, a truncated
        // response) would otherwise slip past the per-store catch blocks and
        // crash deep in a store on `data.data`. Turn it into a rejection so the
        // existing error handling renders a "couldn't load" state instead.
        // `EmptyDataResponse` ({ data: null, result }) is still an object and
        // passes; HEAD / 204 / 205 / 304 carry no body by design.
        const method = response.config.method?.toUpperCase()
        const bodyExpected =
          method !== 'HEAD' &&
          response.status !== 204 &&
          response.status !== 205 &&
          response.status !== 304
        if (
          bodyExpected &&
          (response.data == null || typeof response.data !== 'object')
        ) {
          return Promise.reject(new EmptyResponseError(response))
        }
        return response
      },
      (error) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status ?? 0
          const config = error.config as CustomAxiosRequestConfig | undefined
          if (this.errorListener && (!config?.expected404 || status !== 404)) {
            this.errorListener(
              error,
              error.config?.headers[CORRELATION_ID_HEADER] ?? 'unknown'
            )
          }
          if (status === 503) {
            eventBus.emit('showMaintenance')
          }
        }

        return Promise.reject(error)
      }
    )
  }

  private mountRefreshTokenExpirationInterceptor() {
    this.axiosInstance.interceptors.response.use((response) => {
      const expiration = response.headers['x-refresh-token-expiration']
      if (expiration) {
        TokenService.updateRefreshTokenExpiration(Number(expiration))
      }
      return response
    })
  }

  public mount401Interceptor() {
    // Guard against mounting twice: a second copy would handle the same 401
    // again, i.e. run another refresh and another logout after the first fails.
    if (this._401interceptor !== null) {
      return
    }

    this._401interceptor = this.axiosInstance.interceptors.response.use(
      (response) => {
        return response
      },
      async (error) => {
        const originalRequest = error.config as
          | CustomAxiosRequestConfig
          | undefined

        if (
          error.message === CANCEL_REQUEST_MESSAGE ||
          error.response?.status !== 401 ||
          error.config?._retry ||
          error.config?.skip401Interceptor
        ) {
          throw error
        }

        if (!originalRequest || !this.isApiDestination(originalRequest)) {
          throw error
        }

        originalRequest.headers = originalRequest.headers ?? {}

        try {
          const newToken = await this.ensureRefresh()
          originalRequest.headers[AUTH_HEADER] = newToken
          originalRequest._retry = true
          return this.axiosInstance(originalRequest)
        } catch {
          throw error
        }
      }
    )
  }

  public unmount401Interceptor() {
    // Eject the interceptor
    if (this._401interceptor !== null) {
      this.axiosInstance.interceptors.response.eject(this._401interceptor)
      this._401interceptor = null
    }
  }

  public setErrorListener(cb: ErrorListenerCallback) {
    this.errorListener = cb
  }

  public setSessionHandlers(handlers: SessionHandlers) {
    this.sessionHandlers = handlers
  }

  private static getHash(path: string, method: string = 'GET'): string {
    const hash = new JsSHA1('SHA-1', 'TEXT', { encoding: 'UTF8' })
    hash.update(`${method}:${path}`)
    return hash.getHash('HEX')
  }
}

export default ApiService.getInstance()
